'use client'

import { FC } from 'react'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  type SystemStyleObject,
  VStack
} from '@chakra-ui/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ContentContainer } from 'ui/common'
import { useUIGlobalsContext } from 'ui/lib/ui-globals/hooks'
import {
  externalLinkProps,
  MenuItem,
  MenuItemChild
} from '#/src/custom/layout/custom-layout-components/TopNav/MenuItem'

export interface ISlideOutMobileMenuProps {
  menuItems: MenuItem[]
}

// One text style for every row, matching the desktop nav (body font, medium, no uppercase).
const rowTextStyles: SystemStyleObject = {
  fontFamily: 'body',
  fontSize: '1rem',
  fontWeight: 500,
  lineHeight: 1.4,
  textTransform: 'none',
  textAlign: 'left'
}

const rowStyles: SystemStyleObject = {
  ...rowTextStyles,
  display: 'flex',
  alignItems: 'center',
  w: 'full',
  px: 4,
  py: 3.5,
  color: 'text.default',
  borderRadius: 'button',
  transition: 'background 0.2s ease',
  _hover: { bg: 'background.surface' }
}

const MobileMenuRowLink: FC<{
  text: string
  path: string
  isExternal?: boolean
  isCurrent: boolean
  isSubItem?: boolean
}> = ({ text, path, isExternal, isCurrent, isSubItem }) => (
  <Box
    as={Link}
    href={path}
    aria-current={isCurrent ? 'page' : undefined}
    sx={{
      ...rowStyles,
      ...(isSubItem ? { py: 2.5, pl: 7, color: 'text.secondary' } : {}),
      ...(isCurrent ? { color: 'text.default', fontWeight: 600 } : {})
    }}
    {...(isExternal ? externalLinkProps : {})}
  >
    {text}
  </Box>
)

const SlideOutMobileMenu: FC<ISlideOutMobileMenuProps> = ({ menuItems }) => {
  const pathname = usePathname()
  const { menuMaxContentWidth } = useUIGlobalsContext()

  const isSubCurrent = (sub: MenuItemChild) => pathname === sub.path

  return (
    <ContentContainer direction="column" p={0} maxW={menuMaxContentWidth}>
      {/* rows keep the order set in SFA; dropdown entries expand in place */}
      <Accordion allowToggle w="full">
        <VStack align="stretch" spacing={0}>
          {menuItems.map((item) => {
            if (!item.subItems?.length) {
              return item.path ? (
                <MobileMenuRowLink
                  key={item.id}
                  text={item.text}
                  path={item.path}
                  isExternal={item.isExternal}
                  isCurrent={pathname === item.path}
                />
              ) : null
            }

            const isSectionCurrent =
              (!!item.path && pathname === item.path) || item.subItems.some(isSubCurrent)

            return (
              <AccordionItem key={item.id} border="none">
                <AccordionButton
                  sx={{
                    ...rowStyles,
                    ...(isSectionCurrent ? { fontWeight: 600 } : {}),
                    _expanded: { bg: 'background.surface' },
                    _focusVisible: { boxShadow: 'outline' }
                  }}
                >
                  <Box as="span" flex={1}>
                    {item.text}
                  </Box>
                  <AccordionIcon color="text.secondary" />
                </AccordionButton>
                <AccordionPanel p={0} pb={2}>
                  {/* the entry's own page; the button above only toggles */}
                  {item.path ? (
                    <MobileMenuRowLink
                      text="Overview"
                      path={item.path}
                      isExternal={item.isExternal}
                      isCurrent={pathname === item.path}
                      isSubItem
                    />
                  ) : null}
                  {item.subItems.map((sub) => (
                    <MobileMenuRowLink
                      key={sub.id}
                      text={sub.text}
                      path={sub.path}
                      isExternal={sub.isExternal}
                      isCurrent={isSubCurrent(sub)}
                      isSubItem
                    />
                  ))}
                </AccordionPanel>
              </AccordionItem>
            )
          })}
        </VStack>
      </Accordion>
    </ContentContainer>
  )
}

export default SlideOutMobileMenu
